import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, NavigationHeader, SettingsItemNavigate, Spacer } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { savePotsOnboardingState } from 'features/pots/actions';
import { getPeriodFromRRule } from 'features/pots/utils';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { OnboardingFlow } from 'utils/types/navigation';
const AutosaveKnowContributionAmount = ({ route, navigation }) => {
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const dispatch = useAppDispatch();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const collectionFrequency = useAppSelector((state) => state.pots.onboarding?.collectionFrequency);
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const period = collectionFrequency ? getPeriodFromRRule(collectionFrequency.rrule) : 'Weekly';
    const scrollY = useSharedValue(0);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const handleYes = useCallback(() => {
        if (!collectionFrequency) {
            return;
        }
        dispatch(savePotsOnboardingState({ automated: null }));
        navigation.navigate('AutosaveChooseCustomContributions', { collectionFrequency });
    }, [collectionFrequency]);
    const handleNo = useCallback(() => {
        if (!collectionFrequency) {
            return;
        }
        navigation.navigate('AutosaveEnableAISavings', { collectionFrequency });
    }, [collectionFrequency]);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Pots}/> : undefined} rightContent={<View style={mountedOnAnotherScreen ? styles.rightContent : null}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title={`Do you know how much you’d like to contribute ${period === 'Monthly' ? 'monthly' : 'weekly'}?`}/>
        <Spacer h={8}/>
        <TouchableBounceCard noMarginTop onDark onPress={handleYes}>
          <View pointerEvents="none">
            <SettingsItemNavigate title="Yes" titleFirst description="You set the amount"/>
          </View>
        </TouchableBounceCard>
        <TouchableBounceCard noMarginTop onDark onPress={handleNo}>
          <View pointerEvents="none">
            <SettingsItemNavigate title="No" titleFirst description="We'll work it out for you"/>
          </View>
        </TouchableBounceCard>
      </Animated.ScrollView>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    rightContent: {
        marginRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
}));
export default AutosaveKnowContributionAmount;
